import { memo, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Masonry from "@mui/lab/Masonry";

import CompetitorGalleryItem from "./CompetitorGalleryItem";
import { MODAL_COMPETITORS_GALLERY_PADDINGS } from "../constants";
import { getCompetitorFolderContentID, getCompetitorItemId } from "../utils";

const NOOP = () => {};
const DEFAULT_FAVORITE_STATE = { isFavorite: false, isLoading: false };

const CompetitorsGallery = memo(function CompetitorsGallery({
  items = [],
  isMenuMode = false,
  isCreateWithIAMode = false,
  acceptedReferenceMediaType = null,
  selectedIndexes = [],
  selectedContentIDs = [],
  draggableForFolders = false,
  uploadingItemId = null,
  directAddLabel,
  isUploading = false,
  onSelectItem = NOOP,
  onSelectPreview = NOOP,
  onDirectGenerateItem = NOOP,
  onToggleFavoriteItem = NOOP,
  onCensoredItem = NOOP,
  getFavoriteState = () => DEFAULT_FAVORITE_STATE,
}) {
  const [unmutedVideoKey, setUnmutedVideoKey] = useState(null);
  const selectedIndexSet = useMemo(
    () => new Set(selectedIndexes),
    [selectedIndexes]
  );
  const selectedContentIDSet = useMemo(
    () => new Set(selectedContentIDs),
    [selectedContentIDs]
  );

  const onRequestVideoUnmute = useCallback(({ videoMuteKey }) => {
    setUnmutedVideoKey(videoMuteKey);
  }, []);

  const onRequestVideoMute = useCallback(() => {
    setUnmutedVideoKey(null);
  }, []);

  const onSelectPreviewWithMute = useCallback(
    (previewParams) => {
      setUnmutedVideoKey(null);
      onSelectPreview(previewParams);
    },
    [onSelectPreview]
  );

  return (
    <Box
      sx={{
        pl: MODAL_COMPETITORS_GALLERY_PADDINGS.pl,
        pr: MODAL_COMPETITORS_GALLERY_PADDINGS.pr,
        pt: 2,
        pb: 2,
        overflowAnchor: "none",
      }}
    >
      <Masonry columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing={2}>
        {items.map((item, index) => {
          const itemKey =
            getCompetitorItemId(item) || item.url || `competitor-item-${index}`;
          const folderContentID = getCompetitorFolderContentID(item);

          const videoMuteKey = `${itemKey}::${item?.url || ""}`;

          return (
            <Box key={itemKey} sx={{ flexShrink: 0 }}>
              <CompetitorGalleryItem
                item={item}
                index={index}
                isSelected={selectedIndexSet.has(index)}
                folderContentID={folderContentID}
                draggableForFolders={draggableForFolders}
                isPartOfDragSelection={
                  !!folderContentID && selectedContentIDSet.has(folderContentID)
                }
                selectedDragCount={selectedContentIDs.length}
                isMenuMode={isMenuMode}
                isCreateWithIAMode={isCreateWithIAMode}
                acceptedReferenceMediaType={acceptedReferenceMediaType}
                uploadingItemId={uploadingItemId}
                directAddLabel={directAddLabel}
                isUploading={isUploading}
                videoMuteKey={videoMuteKey}
                unmutedVideoKey={unmutedVideoKey}
                onRequestVideoUnmute={onRequestVideoUnmute}
                onRequestVideoMute={onRequestVideoMute}
                onSelectItem={onSelectItem}
                onSelectPreview={onSelectPreviewWithMute}
                onDirectGenerateItem={onDirectGenerateItem}
                onToggleFavoriteItem={onToggleFavoriteItem}
                onCensoredItem={onCensoredItem}
                getFavoriteState={getFavoriteState}
              />
            </Box>
          );
        })}
      </Masonry>
    </Box>
  );
});

export default CompetitorsGallery;
